import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IAttributesResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { IAttributeData } from "../../views/Estimating/Configuration/Attributes/Attributes.types";

const GET_ATTRIBUTES_UUID = createUUID();
const GET_RECENT_ATTRIBUTES_UUID = createUUID();

export const useAttributes = () => {
  const [attributes, setAttributes] = useState<IAttributesResponse[]>([]);
  const [recentAttributes, setRecentAttributes] = useState<
    IAttributesResponse[]
  >([]);
  const [refresh, setRefresh] = useState(false);

  const stateManager = useComponentState();

  const getAttributes = async ({
    top,
    skip,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_ATTRIBUTES_UUID);

    const response = await stateManager.run(async () => {
      return homeService.attributes.getAll({
        sort: "name",
        top,
        skip,
        filters: { name: { contains: search } },
      });
    }, GET_ATTRIBUTES_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setAttributes(response.data);
    }
    return response;
  };

  const getRecentAttributes = async ({
    sort,
    filters,
    top,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_RECENT_ATTRIBUTES_UUID);

    const response = await stateManager.run(async () => {
      return homeService.attributes.getAll({
        sort,
        top,
        filters: {
          ...filters,
          name: { contains: search },
        },
      });
    }, GET_RECENT_ATTRIBUTES_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setRecentAttributes(response.data);
    }
    return response;
  };

  const getAttributeDetails = (id: string) => {
    return stateManager.run(() => {
      return homeService.attributes.getById({ id });
    });
  };

  const getAttributeSearchOptions = (
    attributeId: string,
    categoryId: string,
    searchText?: string
  ) => {
    return stateManager.run(() => {
      return homeService.attributes.getAttributesById({
        attributeId,
        categoryId,
        searchText,
      });
    });
  };

  const createAttribute = (data: IAttributeData) => {
    return stateManager.run(() => {
      return homeService.attributes.create({
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully created Attribute: ${data.name}.`,
          },
        },
      });
    });
  };

  const updateAttribute = (data: Partial<IAttributeData>) => {
    return stateManager.run(() => {
      return homeService.attributes.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully updated Attribute: ${data.name}.`,
          },
        },
      });
    });
  };

  const deleteAttribute = (data: Partial<IAttributeData>) => {
    stateManager.run(async () => {
      const response = await homeService.attributes.delete({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    attributes,
    recentAttributes,
    refresh,
    getAttributes,
    getRecentAttributes,
    getAttributeDetails,
    getAttributeSearchOptions,
    setAttributes,
    setRecentAttributes,
    createAttribute,
    updateAttribute,
    deleteAttribute,
  };
};
